import { z } from 'zod'
import { blackoutSchema } from './blackout.js'
import { leaveStatusSchema, leaveTypeIdSchema } from './leave.js'
import { publicHolidaySchema } from './public-holiday.js'

/** A single half-day slot on a leave: which date, and AM (morning) or PM
 *  (afternoon). Non-sensitive day structure — carries no type/reason. */
export const calendarHalfDaySchema = z.object({
  date: z.string().date(),
  slot: z.enum(['AM', 'PM']),
})
export type CalendarHalfDay = z.infer<typeof calendarHalfDaySchema>

export const calendarLeaveSchema = z.object({
  id: z.guid(),
  employeeId: z.guid(),
  // Preferred name when set, else the full name.
  employeeName: z.string(),
  // Raw preferred name, so calendar bars show it in full and shorten only the
  // full name (see `calendarName`). Optional for older fixtures/payloads.
  employeePreferredName: z.string().nullable().optional(),
  teamId: z.guid(),
  teamName: z.string(),
  // 'hidden' is the privacy sentinel the API returns when the viewer may not
  // see the leave type (employees see real types only for their own leaves).
  type: z.union([leaveTypeIdSchema, z.literal('hidden')]),
  startDate: z.string().date(),
  endDate: z.string().date(),
  halfDay: z.boolean(),
  // Day structure for the per-day breakdown, visible to anyone who can see the
  // leave (never reveals type/reason). `halfStart`/`halfEnd` mark a boundary
  // half; `halfDays` carries the precise per-date AM/PM slots. `halfDayAllowed`
  // says whether this leave type gets a working-day breakdown at all (mirrors
  // leave_types.half_day_allowed) — sent so the UI can gate without the type.
  // Optional (tolerant reader): the API always sends them, but callers that
  // synthesise a calendar leave locally may omit them; consumers default.
  halfStart: z.boolean().optional(),
  halfEnd: z.boolean().optional(),
  halfDays: z.array(calendarHalfDaySchema).optional(),
  halfDayAllowed: z.boolean().optional(),
  reason: z.string().nullable().optional(),
  status: leaveStatusSchema,
})
export type CalendarLeave = z.infer<typeof calendarLeaveSchema>
export { leaveStatusSchema as calendarLeaveStatuses }

export const calendarPayloadSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/, 'month must be YYYY-MM'),
  leaves: z.array(calendarLeaveSchema),
  blackouts: z.array(blackoutSchema),
  publicHolidays: z.array(publicHolidaySchema),
})
export type CalendarPayload = z.infer<typeof calendarPayloadSchema>
